import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      // Retry up to 5 times with increasing delays.
      // Emergent Auth session data may not be instantly available after redirect.
      const maxRetries = 5;
      const delays = [0, 1500, 2500, 3000, 3000];

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          await new Promise(r => setTimeout(r, delays[attempt]));
        }

        try {
          // Attach first-touch UTM attribution so Google-OAuth signups are
          // also tracked back to their original traffic source.
          let utm = {};
          try {
            const stored = sessionStorage.getItem("fg_utm_attribution");
            if (stored) utm = JSON.parse(stored) || {};
          } catch { /* sessionStorage unavailable */ }

          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId, ...utm },
            { timeout: 10000 }
          );

          if (response.data.session_token) {
            localStorage.setItem("session_token", response.data.session_token);
          }

          setUser(response.data);
          window.history.replaceState(null, "", window.location.pathname);

          if (!response.data.nonprofit_profile?.org_name) {
            navigate("/profile", { replace: true, state: { setup: true } });
          } else {
            navigate("/dashboard", { replace: true });
          }
          return;
        } catch (error) {
          console.warn(`Auth attempt ${attempt + 1}/${maxRetries} failed`);
          if (attempt === maxRetries - 1) {
            console.error("Auth failed after all retries:", error);
            window.history.replaceState(null, "", window.location.pathname);
            navigate("/", { replace: true });
          }
        }
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="loading-spinner mx-auto mb-4" />
        <p className="text-zinc-400 text-sm font-mono" data-testid="auth-status">AUTHENTICATING...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
